/**
 * Hook für Active Schedule
 * 
 * Prüft ob eine Entity einen aktiven oder geplanten Schedule hat.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  getActiveSchedule,
  checkScheduleConflict,
  cancelSchedule,
  cancelContentBlockSchedule,
  cancelChannelPackageSchedule,
} from '@/lib/api/publishing-schedules'
import type { ScheduleEntityType, PublishingSchedule } from '@/types/publishing'

interface UseActiveScheduleOptions {
  entityType: ScheduleEntityType
  entityId: string
  channelId?: string  // Required for channel_package
  enabled?: boolean
}

/**
 * Hook to check for active schedule on an entity
 */
export function useActiveSchedule({
  entityType,
  entityId,
  enabled = true,
}: UseActiveScheduleOptions) {
  return useQuery({
    queryKey: ['schedule', 'active', entityType, entityId],
    queryFn: () => getActiveSchedule(entityType, entityId),
    enabled: enabled && !!entityId,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  })
}

/**
 * Hook to check for schedule conflicts before creating new schedule
 */
export function useScheduleConflict({
  entityType,
  entityId,
  enabled = true,
}: UseActiveScheduleOptions) {
  return useQuery({
    queryKey: ['schedule', 'conflict', entityType, entityId],
    queryFn: () => checkScheduleConflict(entityType, entityId),
    enabled: enabled && !!entityId,
    staleTime: 10_000, // 10 seconds
  })
}

/**
 * Hook for canceling a schedule
 */
export function useCancelSchedule(
  entityType: ScheduleEntityType,
  entityId: string,
  channelId?: string
) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      switch (entityType) {
        case 'content_block':
          return cancelContentBlockSchedule(entityId, scheduleId)
        case 'channel_package':
          if (!channelId) throw new Error('channelId required for channel_package')
          return cancelChannelPackageSchedule(channelId, entityId, scheduleId)
        default:
          return cancelSchedule(scheduleId)
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.scheduleCancelled'))
        // Invalidate related queries
        queryClient.invalidateQueries({ 
          queryKey: ['schedule', 'active', entityType, entityId] 
        })
        queryClient.invalidateQueries({ 
          queryKey: ['schedule', 'conflict', entityType, entityId] 
        })
        queryClient.invalidateQueries({ 
          queryKey: ['schedules'] 
        })
      } else {
        toast.error(data.error || t('toast.error.cancelling'))
      }
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`)
    },
  })
}

/**
 * Calculate time remaining until schedule ends
 */
export function useTimeRemaining(endDate: string | null | undefined): {
  isEnded: boolean
  isStarted: boolean
  days: number
  hours: number
  minutes: number
  seconds: number
  formatted: string
} {
  if (!endDate) {
    return {
      isEnded: true,
      isStarted: false,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      formatted: '',
    }
  }

  const now = new Date()
  const end = new Date(endDate)
  const diff = end.getTime() - now.getTime()

  if (diff <= 0) {
    return {
      isEnded: true,
      isStarted: true,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      formatted: 'Abgelaufen',
    }
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  let formatted = ''
  if (days > 0) {
    formatted = `${days}d ${hours}h`
  } else if (hours > 0) {
    formatted = `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    formatted = `${minutes}m ${seconds}s`
  } else {
    formatted = `${seconds}s`
  }

  return {
    isEnded: false,
    isStarted: true,
    days,
    hours,
    minutes,
    seconds,
    formatted,
  }
}

/**
 * Format schedule status for display
 */
export function getScheduleDisplayInfo(schedule: PublishingSchedule | null): {
  isActive: boolean
  isScheduled: boolean
  statusLabel: string
  statusColor: string
} {
  if (!schedule) {
    return {
      isActive: false,
      isScheduled: false,
      statusLabel: '',
      statusColor: '',
    }
  }

  switch (schedule.status) {
    case 'active':
      return {
        isActive: true,
        isScheduled: false,
        statusLabel: 'Aktiv',
        statusColor: 'warning',
      }
    case 'scheduled':
      return {
        isActive: false,
        isScheduled: true,
        statusLabel: 'Geplant',
        statusColor: 'info',
      }
    case 'completed':
      return {
        isActive: false,
        isScheduled: false,
        statusLabel: 'Abgeschlossen',
        statusColor: 'success',
      }
    case 'canceled':
      return {
        isActive: false,
        isScheduled: false,
        statusLabel: 'Abgebrochen',
        statusColor: 'secondary',
      }
    default:
      return {
        isActive: false,
        isScheduled: false,
        statusLabel: '',
        statusColor: '',
      }
  }
}


